import {ActionTree} from "vuex";
import {WebConferencesList, WebConferenceState} from "@share/web-conferences/store/WebConferenceState";
import {RootState} from "@service/store/types/RootState";
import {JoinedUserId} from "@share/web-conferences/types/JoinedUserId";
import {SetPresentersParam} from "@share/web-conferences/types/SetPresentersParam";
import {AddPresentersParam} from "@share/web-conferences/types/AddPresentersParam";
import {SetAgoraParams} from "@share/web-conferences/types/SetAgoraParams";
import {StoreParameters} from "@share/web-conferences/types/StoreParameters";
import {UserProfile} from "@api/models/UserProfile";
import {addImmutably, removeImmutably} from "@common/ObjectsHelper";
import {AgoraInterface} from "@share/web-conferences/interfaces/AgoraInterface";
import {WebConferenceJoinResult} from "@api/models/web-conferences/WebConferenceJoinResult";
import { WebConferenceStateHelper } from "../helpers/WebConferenceStateHelper";
import { TracksManagerCommonInterface } from "@share/web-conferences/types/TracksManagerCommonInterface";

export const addJoinedUserAction = 'webConference/addJoined';
export const removeJoinedUserAction = 'webConference/removeJoined';
export const cleanWebConferenceAction = 'webConference/clean';
export const setPresentersAction = 'webConference/setPresenters';
export const addPresenterAction = 'webConference/addPresenter';
export const removePresenterAction = 'webConference/removePresenter';
export const setAgoraAction = 'webConference/setAgora';
export const setStateHelper = 'webConference/setStateHelper';
export const addRaisedHandAction = 'webConference/addRaisedHand';
export const setRaisedHandAction = 'webConference/setRaisedHand';
export const removeRaisedHandAction = 'webConference/removeRaisedHand';
export const addRemovedSpeakers = 'webConference/addRemovedSpeakers';
export const removeFromRemovedSpeakers = 'webConference/removeFromRemovedSpeakers';
export const setRecordingStartedAction = 'webConference/setRecordingStarted';
export const setWebConferenceDataAction = 'webConference/setWebConferenceData';
export const setTracksManager = 'webConference/setTracksManager';

// device state
export const setSelectedAudioDevice = 'webConference/setSelectedAudioDevice';
export const setSelectedVideoDevice = 'webConference/setSelectedVideoDevice';
export const setMicIsOn = 'webConference/setMicIsOn';
export const setVideoIsOn = 'webConference/setVideoIsOn';

export const actions: ActionTree<WebConferenceState, RootState> = {
    addJoined({commit, state}, data: JoinedUserId) {
        if (data.userId) {
            let newJoinedIds = [data.userId];
            if (state.joinedUserIds[data.key]) {
                newJoinedIds = [...state.joinedUserIds[data.key], ...newJoinedIds];
            }

            commit('setJoinedUserIds', addImmutably(state.joinedUserIds, data.key, newJoinedIds));
        }
    },
    removeJoined({commit, state}, data: JoinedUserId) {
        if (!state.joinedUserIds[data.key] || !data.userId) {
            return;
        }
        const filteredList = state.joinedUserIds[data.key].filter(userId => userId != data.userId);
        commit('setJoinedUserIds', addImmutably(state.joinedUserIds, data.key, filteredList))
    },
    setPresenters({commit, state}, data: SetPresentersParam) {
        const newPresenters = addImmutably(state.presenters, data.key, data.users);
        commit('setPresenters', newPresenters);
    },
    addPresenter({commit, state, dispatch}, data: AddPresentersParam) {
        if (data.user && state.presenters[data.key]) {
            commit('setPresenters', addImmutably(state.presenters, data.key, [...state.presenters[data.key], data.user]));
        }
        dispatch('removeFromRemovedSpeakers', {
            key: data.key,
            data: [data.user.userId]
        });
    },
    removePresenter({commit, state, dispatch}, data: AddPresentersParam) {
        if (!state.presenters[data.key] || !data.user) {
            return;
        }
        const filteredList = state.presenters[data.key].filter(profile => profile.userId != data.user.userId);
        commit('setPresenters', addImmutably(state.presenters, data.key, filteredList));

        dispatch('addRemovedSpeakers', {
            key: data.key,
            data: [data.user.userId]
        });
    },
    clean({commit, state}, key: string) {
        const map = {
            'joinedUserIds': 'setJoinedUserIds',
            'presenters': 'setPresenters',
            'raisedHandUsers': 'setRaisedHandUsers',
            'recordingStarted': 'setRecordingStarted',
            'webConferenceData': 'setWebConferenceData',
            'tracksManager': 'setTracksManager'
        }

        for (const [property, mutation] of Object.entries(map)) {
            commit(mutation, removeImmutably(state[property as keyof WebConferenceState] as Record<string, any>, key));
        }

        if (state.agora) {
            commit('setAgora', removeImmutably(state.agora, key));
        }
    },
    //@todo refactor params to StoreParameters
    setAgora({commit, state}, data: SetAgoraParams) {
        const newAgora = addImmutably<WebConferencesList<AgoraInterface>>(state.agora ?? {}, data.key, data.agora);
        commit('setAgora', newAgora);
    },
    setWebConferenceData({commit, state}, params: StoreParameters<WebConferenceJoinResult>) {
        commit('setWebConferenceData', addImmutably(state.webConferenceData, params.key, params.data));
    },

    setTracksManager({commit, state}, params: StoreParameters<TracksManagerCommonInterface>) {
        commit('setTracksManager', addImmutably(state.tracksManager, params.key, params.data));
    },

    setStateHelper({ state }, data: WebConferenceStateHelper) {
        state.stateHelper = data;
    },

    setRaisedHand({ commit, state }, params: StoreParameters<UserProfile[]>) {
        commit('setRaisedHandUsers', addImmutably(state.raisedHandUsers, params.key, params.data));
    },
    addRaisedHand({commit, state, dispatch, rootGetters}, params: StoreParameters<UserProfile[]>) {
        const currentStore = [...state.raisedHandUsers[params.key] ?? []];
        params.data.forEach(item => {
            if (currentStore.findIndex(currentStoreItem => item.userId == currentStoreItem.userId) < 0) {
                currentStore.push(item);
            }
        });
        commit('setRaisedHandUsers', addImmutably(state.raisedHandUsers, params.key, currentStore));
    },
    removeRaisedHand({commit, state}, params: StoreParameters<UserProfile[]>) {
        const userIdsToRemove = params.data.map(item => item.userId);
        const dataWithoutDeleted = [...state.raisedHandUsers[params.key] ?? []].filter(item => {
            return userIdsToRemove.indexOf(item.userId) < 0;
        });
        commit('setRaisedHandUsers', addImmutably(state.raisedHandUsers, params.key, dataWithoutDeleted));
    },
    addRemovedSpeakers({commit, state}, data: StoreParameters<number | number[]>) {
        const dataToAdd = Array.isArray(data.data) ? [...data.data] : [data.data];
        if (dataToAdd.length) {
            const currentData = state.removedSpeakers[data.key] || [];

            commit('setRemovedSpeakers', addImmutably(state.removedSpeakers, data.key, [...currentData, ...dataToAdd]));
        }
    },
    removeFromRemovedSpeakers({commit, state}, data: StoreParameters<number | number[]>) {
        const dataToRemove = Array.isArray(data.data) ? [...data.data] : [data.data];
        if (dataToRemove.length) {
            const currentData = state.removedSpeakers[data.key] || [];
            const filteredList = currentData.filter(item => dataToRemove.indexOf(item) < 0);

            commit('setRemovedSpeakers', addImmutably(state.removedSpeakers, data.key, filteredList));
        }
    },
    setRecordingStarted({commit, state}, param: StoreParameters<boolean>) {
        commit('setRecordingStarted', addImmutably(state.recordingStarted, param.key, param.data));
    },
    setSelectedAudioDevice({ state }, payload: string) {
        state.deviceTestState.selectedDevices.audioDeviceId = payload;
    },
    setSelectedVideoDevice({ state }, payload: string) {
        state.deviceTestState.selectedDevices.videoDeviceId = payload;
    },
    setMicIsOn({ state }, payload: boolean) {
        state.deviceTestState.deviceState.micIsOn = payload;
    },
    setVideoIsOn({ state }, payload: boolean) {
        state.deviceTestState.deviceState.videoIsOn = payload;
    }
}
